/**
 * Identifier for "what kind of thing is being filtered". Conventionally an ENPS
 * object-type name (e.g. 'Assignment', 'Story', 'Rundown'), but free-form so callers
 * can use any identifier for non-object-type sections (e.g. 'Attachment').
 */
export type FilterObjectType = string;

/** Filter object-type identifiers as declared by the backend via FieldDef.SectionFilter. */
export const FilterObjectTypes = {
    ASSIGNMENT: 'Assignment',
    STORY: 'Story',
    STORY_TEMPLATE: 'Story Template',
} as const;

/** Identifiers for the UI surface a saved filter state belongs to. */
export const FilterSections = {
    TOPIC_FULL_SCREEN: 'TopicFullScreen',
} as const;

/** Wildcard section: an enriched config that applies to every section. */
export const FILTER_SECTION_ALL = '*';

/**
 * A single filter's value as persisted.
 * - `LookupValue[]` — checklist / combobox / lookuplist selections
 * - `string[]` — staffidlist (user IDs)
 * - `string` — checkbox-like (e.g. 'today', 'asc', OPTION_ALL)
 * - `StorytellingDateRange` — daterange / datetimerange
 * - `null` — cleared
 */
export type FilterSavedValue = string | string[] | LookupValue | LookupValue[] | StorytellingDateRange | null;

/** The user's selections for one (objectType, section) pair, keyed by filter ID. */
export type FilterSavedState = Record<string, FilterSavedValue>;

/** Full payload returned in HelloResults (JSON-stringified on the wire). */
export type SectionFiltersPayload = Record<string, Record<string, FilterSavedState>>;

export interface InlineFilterState {
    /** Last state successfully persisted to the server (or hydrated from HelloResults). */
    persistedStates: SectionFiltersPayload;
    /** In-memory state currently driving the UI; mutated by user edits. */
    workingStates: SectionFiltersPayload;
    enrichedConfigs: Record<string, StorytellingFilterDefinition[]>;
}

export const enrichedConfigKey = (objectType: FilterObjectType, section: string): string => `${objectType}:${section}`;
